<script setup lang="ts">
import { MediaImageFragment, ParagraphMap } from '~/api/graphql/generated'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  entity: ParagraphMap
}>()

const position = computed(() => {
  return {
    lat: props.entity.fieldLatitude,
    lng: props.entity.fieldLongitude,
  }
})

const image = computed(() => props.entity?.fieldImage?.entity as MediaImageFragment)

const address = computed(() => {
  const url = `https://www.google.com/maps/search/?api=1&query=${props.entity.fieldLatitude},${props.entity.fieldLongitude}`
  return {
    url,
    html: `<span>${props.entity.fieldStreet}</span><br/><span class="address-second-line">${props.entity.fieldZipCode} ${props.entity.fieldLocation}</span>`,
  }
})
</script>

<template>
  <Section v-animate="{ animation: 'vertical-reveal' }" large>
    <GoogleMap v-if="position.lat && position.lng" :lat="position.lat" :lng="position.lng" />
    <div class="info">
      <Image v-if="image" :entity="image" type="sidebar-logo" />
      <h5 v-if="props.entity.fieldSubtitle">{{ props.entity.fieldSubtitle }}</h5>
      <p v-if="address.html" class="address text-small">
        <a :href="address.url" target="_blank" rel="noopener noreferrer" v-html="address.html" />
      </p>

      <ul class="details cul">
        <li v-if="props.entity.fieldMail">
          <span class="label">{{ t('openJob.detail.mail') }}</span>
          <span>
            <a :href="`mailto:${props.entity.fieldMail}`" class="link-default text-small">
              {{ props.entity.fieldMail }}
            </a>
          </span>
        </li>
        <li v-if="props.entity.fieldPhone">
          <span class="label">{{ t('openJob.detail.phone') }}</span>
          <a :href="`tel:${props.entity.fieldPhone}`" class="link-default text-small">
            {{ props.entity.fieldPhone }}
          </a>
        </li>
        <li v-if="props.entity.fieldInfoTextLabel">
          <span class="label hyphenate"> {{ props.entity.fieldInfoTextLabel }}</span>
          <div class="text-small" v-html="props.entity.fieldText?.processed" />
        </li>
      </ul>
    </div>
  </Section>
</template>
<style scoped lang="scss">
.section {
  @include breakpoint(tl) {
    align-items: center;
  }

  .map {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    @include fluid(--height, 420px, 800px);

    @include breakpoint(tl) {
      width: 100%;
      margin-left: 0;
      grid-row: 1;
      grid-column: 1 / span 9;
    }
  }
}

.info {
  padding: 30px 24px;
  width: calc(50vw + 50%);
  margin-top: -120px;
  background-color: var(--c-white);
  z-index: 1;
  @include breakpoint(tl) {
    margin-top: 0;
    width: 100%;
    grid-column: 8 / span 5;
    grid-row: 1;
    padding: 64px 0 80px 80px;
  }

  .dp-image {
    margin-bottom: 40px;
  }

  h5 {
    margin-bottom: 24px;
  }

  .address {
    margin-bottom: 32px;

    :deep(.address-second-line) {
      padding-left: 6px;
    }
  }

  a {
    font-weight: 500;
  }
}

.details {
  li {
    display: grid;
    grid-template-columns: 100%;
    gap: 6px;
    @include fluid(padding-block, 18px, 24px);
    @include breakpoint(tl) {
      grid-template-columns: 130px 1fr;
      gap: 12px;
    }

    &:not(:last-child) {
      border-bottom: 1px solid var(--c-line-light);

      .label {
        display: grid;
        align-items: center;
      }
    }

    &:last-child {
      span {
        margin-top: 7px;
      }
    }

    .label {
      color: var(--c-primary);
    }
  }
  a {
    justify-self: start;
  }
}
</style>
